import React, { useState } from "react";
import { useFormik } from "formik";
import { Icon, Input, TextArea } from "semantic-ui-react";
import { useNavigate } from "react-router-dom";

import { Schema } from "../../../../../../api";

import {
  initialValues,
  validationSchema,
} from "./CreateSchemaPage.form";

import styles from "./CreateSchemaPage.module.scss";

const schemaCtrl = new Schema();

export function CreateSchemaPage() {
  const navigate = useNavigate();

  const [serverError, setServerError] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: false,

    onSubmit: async (values) => {
      try {
        setServerError(null);

        const schemaData = {
          id: values.id.trim().toUpperCase(),
          name: values.name.trim(),
          description: values.description.trim(),
          order: Number(values.order),
        };

        await schemaCtrl.createSchema(schemaData);

        navigate("/admin/catalog/schema", {
          state: {
            success: "Esquema creado correctamente.",
          },
        });
      } catch (error) {
        console.error("Error al crear esquema:", error);

        setServerError(
          error?.msg ||
            "No fue posible crear el esquema. Intenta nuevamente."
        );

        if (error?.errors) {
          formik.setErrors(error.errors);
        }

        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    },
  });

  const getFieldError = (fieldName) => {
    const shouldShowError =
      (hasSubmitted || formik.touched[fieldName]) &&
      formik.errors[fieldName];

    return shouldShowError ? formik.errors[fieldName] : null;
  };

  const handleCancel = () => {
    if (formik.isSubmitting) {
      return;
    }

    navigate("/admin/catalog/schema");
  };

  const handleInputChange = (event, { name, value }) => {
    formik.setFieldValue(name, value);

    if (hasSubmitted) {
      formik.validateField(name);
    }

    setServerError(null);
  };

  const handleInputBlur = (event) => {
    formik.handleBlur(event);

    if (hasSubmitted) {
      formik.validateField(event.target.name);
    }
  };

  const handleCreate = async () => {
    setHasSubmitted(true);

    const touchedFields = {
      id: true,
      name: true,
      description: true,
      order: true,
    };

    formik.setTouched(touchedFields, false);

    const validationErrors = await formik.validateForm();

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    formik.handleSubmit();
  };

  const isSaving = formik.isSubmitting;

  const showValidationError =
    hasSubmitted && Object.keys(formik.errors).length > 0;

  return (
    <section className={styles.CreateSchemaPage}>
      <header className={styles.PageHeader}>
        <div className={styles.TitleSection}>
          <button
            type="button"
            className={styles.BackButton}
            onClick={handleCancel}
            disabled={isSaving}
            aria-label="Volver al listado de esquemas"
          >
            <Icon name="arrow left" />
          </button>

          <div>
            <p className={styles.Breadcrumb}>
              Catálogo / Esquemas / Nuevo esquema
            </p>

            <h1>Nuevo esquema</h1>

            <p className={styles.Subtitle}>
              Crea un catálogo padre para organizar sus clasificaciones.
            </p>
          </div>
        </div>

        <div className={styles.HeaderActions}>
          <button
            type="button"
            className={styles.CancelButton}
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancelar
          </button>

          <button
            type="button"
            className={styles.PublishButton}
            onClick={handleCreate}
            disabled={isSaving}
          >
            {isSaving ? "Creando..." : "Crear esquema"}
          </button>
        </div>
      </header>

      {showValidationError && (
        <div className={styles.ValidationMessage}>
          <Icon name="warning sign" />

          <div>
            <strong>Revisa la información del esquema.</strong>

            <span>
              Debes corregir los campos obligatorios antes de crear el esquema.
            </span>
          </div>
        </div>
      )}

      {serverError && (
        <div className={styles.ServerErrorMessage}>
          <Icon name="warning sign" />
          <span>{serverError}</span>
        </div>
      )}

      <form
        className={styles.FormContent}
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <section className={styles.SchemaBasicInformation}>
          <header className={styles.SectionHeader}>
            <div>
              <h2>Información del esquema</h2>

              <p>
                Define el catálogo principal que agrupará sus clasificaciones.
              </p>
            </div>
          </header>

          <div className={styles.InfoMessage}>
            <span className={styles.InfoIcon}>i</span>

            <p>
              Ejemplo: para el esquema <strong>Tipo de producto</strong>,
              podrás registrar clasificaciones como Bolsas, Relojes, Ropa y
              Zapatos.
            </p>
          </div>

          <div className={styles.FormGrid}>
            <div className={styles.FormField}>
              <label htmlFor="id">
                Código del esquema <span>*</span>
              </label>

              <Input
                id="id"
                name="id"
                value={formik.values.id}
                placeholder="Ej. TIPO_PRODUCTO"
                error={Boolean(getFieldError("id"))}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={isSaving}
              />

              {getFieldError("id") && (
                <span className={styles.FieldError}>
                  {getFieldError("id")}
                </span>
              )}
            </div>

            <div className={styles.FormField}>
              <label htmlFor="order">
                Orden de visualización <span>*</span>
              </label>

              <Input
                id="order"
                name="order"
                type="number"
                min="1"
                value={formik.values.order}
                placeholder="Ej. 1"
                error={Boolean(getFieldError("order"))}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={isSaving}
              />

              {getFieldError("order") && (
                <span className={styles.FieldError}>
                  {getFieldError("order")}
                </span>
              )}
            </div>

            <div className={`${styles.FormField} ${styles.FullWidth}`}>
              <label htmlFor="name">
                Nombre del esquema <span>*</span>
              </label>

              <Input
                id="name"
                name="name"
                value={formik.values.name}
                placeholder="Ej. Tipo de producto"
                error={Boolean(getFieldError("name"))}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={isSaving}
              />

              {getFieldError("name") && (
                <span className={styles.FieldError}>
                  {getFieldError("name")}
                </span>
              )}
            </div>

            <div className={`${styles.FormField} ${styles.FullWidth}`}>
              <label htmlFor="description">Descripción</label>

              <TextArea
                id="description"
                name="description"
                value={formik.values.description}
                placeholder="Ej. Agrupa valores como Bolsas, Relojes, Ropa y Zapatos."
                rows={5}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={isSaving}
              />

              <div className={styles.TextAreaFooter}>
                {getFieldError("description") ? (
                  <span className={styles.FieldError}>
                    {getFieldError("description")}
                  </span>
                ) : (
                  <span />
                )}

                <span>{formik.values.description.length}/500</span>
              </div>
            </div>
          </div>
        </section>
      </form>
    </section>
  );
}